import { Formik } from 'formik';
import _ from 'lodash';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { Button, Buttons, CancelButton, Form, FormMessage, ModalCard } from '~/components';
import { FieldProjectTaskSelect } from '~/components/ProjectTaskSelect';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useForm } from '~/hooks';
import * as Yup from 'yup';

const Content = styled.div`
  display: grid;
  row-gap: 1.25rem;
`;

const ContentRow = styled.div`
  display: flex;
  align-items: center;
`;

const TaskName = styled.strong`
  margin: 0 0.25rem;
`;

const InlineWrapper = styled.span`
  display: inline-flex;
  vertical-align: middle;
`;

const TaskSelectContainer = styled.div`
  width: 25rem;
`;

function MoveTimeConfirmation({ task, project, onClose, onSaved }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();
  const [{ isSubmitting, status, message }, form] = useForm();

  const handleSearch = useCallback(
    async (q) => {
      const { data } = await api.www
        .workspaces(workspace.id)
        .projects(project.id)
        .tasks()
        .options({ q, projectId: project.id, recordStatusId: 'active' });

      const groupTasksByParent = (tasks) => {
        const byParent = _.groupBy(tasks, 'parentId');
        const buildOptions = (parentId = null, depth = 0) => {
          return _.sortBy(byParent[parentId] || [], 'position').flatMap((t) => [
            { type: 'task', task: t, depth },
            ...buildOptions(t.id, depth + 1),
          ]);
        };
        return buildOptions();
      };

      return groupTasksByParent(data).filter(
        (o) => o.task.id !== task.id && o.task.mode !== 'group' && !o.task.lockTime,
      );
    },
    [workspace.id, api, project.id, task.id],
  );

  const handleSubmit = async (values) => {
    form.submit(values);

    try {
      await api.www
        .workspaces(workspace.id)
        .projects(project.id)
        .tasks(task.id)
        .moveTime({ targetTaskId: values.targetTask.id });

      toast.success('Time entries have been moved.');
      await onSaved();
      form.done();
    } catch ({ message }) {
      form.error({ message });
    }
  };

  return (
    <ModalCard title="Move Time" onClose={onClose}>
      <Formik
        enableReinitialize
        initialValues={{
          targetTask: null,
        }}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          targetTask: Yup.mixed().required().label('Task'),
        })}>
        <Form>
          <ModalCard.Body>
            <Content>
              <ContentRow>
                Move all time entries from <TaskName>{task.name}</TaskName> to another task in this project.
              </ContentRow>
              <ContentRow>
                <InlineWrapper>
                  <TaskSelectContainer>
                    <FieldProjectTaskSelect
                      name="targetTask"
                      project={project}
                      placeholder="Select a task"
                      onSearch={handleSearch}
                    />
                  </TaskSelectContainer>
                </InlineWrapper>
              </ContentRow>
            </Content>
            {status && <FormMessage.Error spaceTop>{message}</FormMessage.Error>}
          </ModalCard.Body>
          <ModalCard.Footer>
            <Buttons align="right">
              <CancelButton onClick={onClose}>Cancel</CancelButton>
              <Button type="submit" isLoading={isSubmitting}>
                Move Time
              </Button>
            </Buttons>
          </ModalCard.Footer>
        </Form>
      </Formik>
    </ModalCard>
  );
}

export default MoveTimeConfirmation;
